import { useMemo, useState, useEffect, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, CheckCircle, Warning, X } from '@phosphor-icons/react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DatePickerPopover } from '@/components/ui/date-picker-popover';
import { useConfirm } from '@/components/ConfirmDialog';
import { ApiError } from '@/api/client';
import {
  addSlot,
  applyTemplate,
  deleteSlot,
  getTemplate,
  previewApplyTemplate,
  updateSlot,
  type PreviewApplyResult,
  type SlotInput,
} from '@/api/templates';
import type { ActivityType } from '@/api/activities';
import { listTeams } from '@/api/teams';
import { getAvailability, listLocations } from '@/api/locations';
import { TemplateCalendar, type SlotChange } from '@/components/schedule/TemplateCalendar';
import { TemplateSlotFormModal } from '@/components/schedule/TemplateSlotFormModal';
import {
  AvailabilityBlockNotice,
  useAvailabilityBlockNotice,
} from '@/components/schedule/AvailabilityBlockNotice';

interface Props {
  slug: string;
  templateId: string;
  onBack: () => void;
  hideHeader?: boolean;
  selectedLocationIds: Set<string>;
  selectedTeamIds: Set<string>;
  selectedZoneIds: Set<string>;
  selectedActivityTypes: Set<ActivityType>;
}

// TemplateEditorView is the calendar canvas for one template. Click empty
// time → create a slot; click an existing slot → edit; drag/resize → reschedule.
// Apply (stamp this template onto a date range) and Delete (remove the
// whole template) live in the toolbar.
export function TemplateEditorView({ slug, templateId, onBack, hideHeader, selectedLocationIds, selectedTeamIds, selectedZoneIds, selectedActivityTypes }: Props) {
  const { user } = useAuth();
  const token = user?.access_token ?? null;
  const { t } = useTranslation();
  const qc = useQueryClient();
  const confirm = useConfirm();

  const { data: template, isLoading } = useQuery({
    queryKey: ['template', slug, templateId],
    queryFn: () => getTemplate(token, slug, templateId),
    enabled: !!slug && !!templateId,
  });

  // Slot create/edit state. `editing` is the slot id being edited; null
  // means create mode (form opens with the seed values from the calendar
  // click). `creating` is the seed values; both can't be set at once.
  const [editingSlotId, setEditingSlotId] = useState<string | null>(null);
  const [creatingSeed, setCreatingSeed] = useState<SlotChange | null>(null);

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ['template', slug, templateId] });

  const addSlotMut = useMutation({
    mutationFn: (input: SlotInput) => addSlot(token, slug, templateId, input),
    onSuccess: () => {
      setCreatingSeed(null);
      invalidate();
    },
  });

  const updateSlotMut = useMutation({
    mutationFn: (args: { id: string; input: SlotInput }) =>
      updateSlot(token, slug, templateId, args.id, args.input),
    onSuccess: () => {
      setEditingSlotId(null);
      invalidate();
    },
  });

  const deleteSlotMut = useMutation({
    mutationFn: (id: string) => deleteSlot(token, slug, templateId, id),
    onSuccess: () => {
      setEditingSlotId(null);
      invalidate();
    },
  });

  // Teams query — drives slot color resolution when a slot has no
  // explicit color override.
  const { data: teamsRes } = useQuery({
    queryKey: ['teams', slug],
    queryFn: () => listTeams(token, slug),
    enabled: !!slug,
  });
  const teams = teamsRes?.teams ?? [];

  const { data: locationsRes } = useQuery({
    queryKey: ['locations', slug],
    queryFn: () => listLocations(token, slug),
    enabled: !!slug,
  });
  const locations = locationsRes?.locations ?? [];

  // Opening-hours overlay. Same shape as the schedule view: when the
  // user filters down to exactly one location, fetch its rules and pass
  // them through to FullCalendar as businessHours. With a single zone
  // additionally selected, fall through to zone-level rules (or back to
  // location-level when the zone has none) — mirrors the server's
  // CheckAvailability precedence. Exceptions don't apply to templates
  // (templates are dateless abstractions).
  const singleLocationId = selectedLocationIds.size === 1
    ? Array.from(selectedLocationIds)[0] ?? null
    : null;
  const singleZoneId = selectedZoneIds.size === 1
    ? Array.from(selectedZoneIds)[0] ?? null
    : null;

  const { data: availability } = useQuery({
    queryKey: ['locations', slug, singleLocationId, 'availability'],
    queryFn: () => getAvailability(token, slug, singleLocationId!),
    enabled: !!slug && !!singleLocationId,
  });

  // Block-notice plumbing — fires when a click/drop/resize lands in a
  // greyed region (outside the location's opening hours).
  const { notice: blockNotice, onBlocked: onCalendarBlocked, clear: clearBlockNotice } =
    useAvailabilityBlockNotice();

  const businessHours = useMemo(() => {
    if (!availability) return undefined;
    const zoneRules = singleZoneId ? availability.rules[singleZoneId] ?? [] : [];
    const locRules = availability.rules[''] ?? [];
    const rules = zoneRules.length > 0 ? zoneRules : locRules;
    if (rules.length === 0) return undefined;
    return rules.map((r) => ({
      daysOfWeek: [r.day_of_week === 7 ? 0 : r.day_of_week],
      startTime: r.start_time,
      endTime: r.end_time,
    }));
  }, [availability, singleZoneId]);

  // Drag/resize handler — translates the calendar's new start/end back
  // into the slot's day_of_week + HH:MM and PATCHes. Throwing here makes
  // FullCalendar revert the visual move (see TemplateCalendar).
  async function handleReschedule(slotId: string, change: SlotChange) {
    const slot = template?.slots.find((s) => s.id === slotId);
    if (!slot) throw new Error('slot not found');
    await updateSlot(token, slug, templateId, slotId, {
      title: slot.title,
      kind: slot.kind,
      day_of_week: change.day_of_week,
      start_time: change.start_time,
      end_time: change.end_time,
      ...(slot.location_id ? { location_id: slot.location_id } : { location_id: null }),
      ...(slot.zone_id ? { zone_id: slot.zone_id } : { zone_id: null }),
      team_id: slot.team_id,
    });
    invalidate();
  }

  async function onDeleteSlot() {
    if (!editingSlotId) return;
    const ok = await confirm({
      title: t('templates.slot.deleteConfirmTitle'),
      body: t('templates.slot.deleteConfirmBody'),
      confirmLabel: t('templates.slot.delete'),
      tone: 'destructive',
    });
    if (!ok) return;
    deleteSlotMut.mutate(editingSlotId);
  }

  if (isLoading || !template) {
    return <p className="text-sm text-foreground/60">{t('common.loading')}</p>;
  }

  const editingSlot = editingSlotId
    ? template.slots.find((s) => s.id === editingSlotId) ?? null
    : null;

  const filteredSlots = template.slots.filter((s) => {
    if (selectedLocationIds.size > 0 && !(s.location_id && selectedLocationIds.has(s.location_id))) return false;
    if (selectedZoneIds.size > 0 && !(s.zone_id && selectedZoneIds.has(s.zone_id))) return false;
    if (selectedTeamIds.size > 0 && !selectedTeamIds.has(s.team_id)) return false;
    if (selectedActivityTypes.size > 0 && !selectedActivityTypes.has(s.kind)) return false;
    return true;
  });

  return (
    <div className="flex flex-col gap-4">
      {!hideHeader && (
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="icon"
            aria-label={t('common.back')}
            onClick={onBack}
          >
            <ArrowLeft weight="bold" />
          </Button>
          <div>
            <h2 className="font-heading text-xl font-extrabold tracking-tight">
              {template.name}
            </h2>
            <p className="text-xs text-foreground/60">
              {t('templates.slotsCount', { count: template.slots.length })}
            </p>
          </div>
        </div>
      )}

      <div className="relative">
      <TemplateCalendar
        slots={filteredSlots}
        allSlots={template.slots}
        teams={teams}
        locations={locations}
        {...(businessHours ? { businessHours } : {})}
        onBlocked={onCalendarBlocked}
        onCreate={(suggestion) => {
          setEditingSlotId(null);
          setCreatingSeed(suggestion);
        }}
        onEdit={(slotId) => {
          setCreatingSeed(null);
          setEditingSlotId(slotId);
        }}
        onReschedule={handleReschedule}
      />
        {blockNotice && (
          <AvailabilityBlockNotice
            key={blockNotice.key}
            reason={blockNotice.reason}
            onDismiss={clearBlockNotice}
          />
        )}
      </div>

      {creatingSeed && (
        <TemplateSlotFormModal
          slug={slug}
          initial={{
            day_of_week: creatingSeed.day_of_week,
            start_time: creatingSeed.start_time,
            end_time: creatingSeed.end_time,
            team_id: '',
          }}
          onSubmit={(input) => addSlotMut.mutateAsync(input)}
          onCancel={() => setCreatingSeed(null)}
          submitting={addSlotMut.isPending}
          error={describeSlotError(addSlotMut.error, t)}
        />
      )}

      {editingSlot && (
        <TemplateSlotFormModal
          slug={slug}
          initial={{
            id: editingSlot.id,
            title: editingSlot.title,
            kind: editingSlot.kind,
            day_of_week: editingSlot.day_of_week,
            start_time: editingSlot.start_time,
            end_time: editingSlot.end_time,
            location_id: editingSlot.location_id ?? null,
            zone_id: editingSlot.zone_id ?? null,
            team_id: editingSlot.team_id,
          }}
          onSubmit={(input) =>
            updateSlotMut.mutateAsync({ id: editingSlot.id, input })
          }
          onCancel={() => setEditingSlotId(null)}
          onDelete={onDeleteSlot}
          submitting={updateSlotMut.isPending}
          deleting={deleteSlotMut.isPending}
          error={describeSlotError(updateSlotMut.error ?? deleteSlotMut.error, t)}
        />
      )}

    </div>
  );
}

function describeSlotError(err: unknown, t: (k: string) => string): string | null {
  if (!err) return null;
  if (err instanceof ApiError) {
    if (err.code === 'schedule_conflict') return t('schedule.toast.createConflict');
    if (err.code === 'forbidden_by_scope') return t('schedule.validation.teamRequired');
    if (err.code === 'invalid_input') return t('locations.errors.generic');
  }
  return t('locations.errors.generic');
}

interface ApplyProps {
  slug: string;
  token: string | null;
  templateId: string;
  onClose: () => void;
}

export function ApplyTemplateModal({ slug, token, templateId, onClose }: ApplyProps) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();

  const [from, setFrom] = useState(() => isoMondayString(new Date()));
  const [to, setTo] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 14);
    return isoMondayString(d);
  });
  const [preview, setPreview] = useState<PreviewApplyResult | null>(null);

  // Locations the user has ticked. `null` means "haven't customized yet";
  // once the location list loads we seed it to every available location
  // so the default reads as "all selected" — the user can then de-select.
  const [pickedLocationIds, setPickedLocationIds] = useState<Set<string> | null>(null);

  const { data: locationsRes } = useQuery({
    queryKey: ['locations', slug],
    queryFn: () => listLocations(token, slug),
    enabled: !!slug,
  });
  const locations = useMemo(() => locationsRes?.locations ?? [], [locationsRes?.locations]);

  useEffect(() => {
    if (pickedLocationIds === null && locations.length > 0) {
      setPickedLocationIds(new Set(locations.map((l) => l.id)));
    }
  }, [locations, pickedLocationIds]);

  // The server treats "all selected" the same as "no filter" (empty
  // array on the wire). Sending the full list adds nothing and would
  // make the filter look used when it isn't.
  const locationFilter = useMemo(() => {
    if (pickedLocationIds === null) return [] as string[];
    if (locations.length === 0) return [] as string[];
    if (pickedLocationIds.size === locations.length) return [] as string[];
    return Array.from(pickedLocationIds);
  }, [pickedLocationIds, locations]);

  const previewMut = useMutation({
    mutationFn: () => previewApplyTemplate(token, slug, templateId, from, to, locationFilter),
    onSuccess: (r) => setPreview(r),
  });

  const applyMut = useMutation({
    mutationFn: () => applyTemplate(token, slug, templateId, from, to, locationFilter),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['activities', slug] });
      onClose();
    },
  });

  function runPreview() {
    setPreview(null);
    previewMut.mutate();
  }

  function onSubmitForm(e: FormEvent) {
    e.preventDefault();
    if (preview && preview.conflicts.length === 0 && preview.planned.length > 0) {
      applyMut.mutate();
    } else {
      runPreview();
    }
  }

  function toggleLocation(id: string) {
    setPickedLocationIds((prev) => {
      const next = new Set(prev ?? locations.map((l) => l.id));
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setPreview(null);
  }

  function selectAllLocations() {
    setPickedLocationIds(new Set(locations.map((l) => l.id)));
    setPreview(null);
  }

  function clearAllLocations() {
    setPickedLocationIds(new Set());
    setPreview(null);
  }

  const blocked = !!preview && preview.conflicts.length > 0;
  const ready = !!preview && preview.conflicts.length === 0 && preview.planned.length > 0;
  const pickedCount = pickedLocationIds?.size ?? locations.length;
  const noLocationsPicked = pickedCount === 0 && locations.length > 0;

  const error = (() => {
    const e = previewMut.error ?? applyMut.error;
    if (!e) return null;
    if (e instanceof ApiError) {
      if (e.code === 'apply_blocked_by_conflicts') return t('templates.applyConflicts');
      if (e.code === 'invalid_input') return t('locations.errors.generic');
    }
    return t('locations.errors.generic');
  })();

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-foreground/30 p-4"
    >
      <div className="flex max-h-[90vh] w-[min(92vw,40rem)] flex-col overflow-hidden rounded-2xl bg-card shadow-xl">
        <header className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="font-heading text-lg font-bold">{t('templates.applyTitle')}</h2>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={t('common.cancel')}
            onClick={onClose}
          >
            <X weight="bold" />
          </Button>
        </header>
        <div className="flex-1 overflow-y-auto">
          <form onSubmit={onSubmitForm} className="grid grid-cols-2 gap-3 px-6 py-4">
            <label className="flex flex-col gap-1.5">
              <span className="font-heading text-sm font-semibold">
                {t('templates.applyFromWeek')}
              </span>
              <DatePickerPopover
                value={from}
                locale={i18n.language}
                snap={isoMondayString}
                onChange={(next) => {
                  setFrom(next);
                  setPreview(null);
                }}
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="font-heading text-sm font-semibold">
                {t('templates.applyToWeek')}
              </span>
              <DatePickerPopover
                value={to}
                locale={i18n.language}
                snap={isoMondayString}
                onChange={(next) => {
                  setTo(next);
                  setPreview(null);
                }}
              />
            </label>

            {locations.length > 0 && (
              <fieldset className="col-span-2 mt-1 rounded-xl border border-border p-3">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <legend className="font-heading text-sm font-semibold">
                    {t('templates.applyLocations')}
                  </legend>
                  <div className="flex items-center gap-1 text-xs">
                    <button
                      type="button"
                      onClick={selectAllLocations}
                      className="rounded-pill px-2 py-0.5 text-foreground/70 hover:bg-muted hover:text-foreground"
                    >
                      {t('templates.applyLocationsSelectAll')}
                    </button>
                    <span className="text-foreground/30">·</span>
                    <button
                      type="button"
                      onClick={clearAllLocations}
                      className="rounded-pill px-2 py-0.5 text-foreground/70 hover:bg-muted hover:text-foreground"
                    >
                      {t('templates.applyLocationsClear')}
                    </button>
                  </div>
                </div>
                <p className="mb-2 text-xs text-foreground/60">
                  {t('templates.applyLocationsHint')}
                </p>
                <ul className="grid max-h-48 grid-cols-1 gap-0.5 overflow-y-auto sm:grid-cols-2">
                  {locations.map((loc) => {
                    const checked = pickedLocationIds?.has(loc.id) ?? true;
                    return (
                      <li key={loc.id}>
                        <label className="flex cursor-pointer items-center gap-2 rounded-lg px-1.5 py-1 hover:bg-muted">
                          <Checkbox checked={checked} onChange={() => toggleLocation(loc.id)} />
                          <span className="truncate text-sm">{loc.name}</span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </fieldset>
            )}

            <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
          </form>

          {noLocationsPicked && (
            <p className="mx-6 mb-3 rounded-xl bg-foreground/5 px-3 py-2 text-xs text-foreground/70">
              {t('templates.applyLocationsNoneSelected')}
            </p>
          )}

          {preview ? (
            <div className="border-t border-border px-6 py-4">
              <p className="text-sm">
                {t('templates.applyPreviewCount', { count: preview.planned.length })}
              </p>
              {blocked && (
                <div className="mt-3 rounded-xl bg-destructive/10 p-3 text-sm text-destructive">
                  <Warning weight="bold" className="inline" />{' '}
                  {[t('templates.applyConflicts'), String(preview.conflicts.length)].join(' — ')}
                  <p className="mt-1 text-xs">{t('templates.applyConflictsBody')}</p>
                </div>
              )}
              {ready && (
                <div className="mt-3 flex items-center gap-2 rounded-xl bg-primary/10 p-3 text-sm">
                  <CheckCircle weight="bold" size={18} className="shrink-0" />
                  <span>{t('templates.applyPreviewReady')}</span>
                </div>
              )}
            </div>
          ) : null}

          {error ? (
            <p
              role="alert"
              className="mx-6 mb-3 rounded-xl bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {error}
            </p>
          ) : null}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-border px-6 py-4">
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          {preview ? (
            <Button
              type="button"
              disabled={!ready || applyMut.isPending}
              onClick={() => applyMut.mutate()}
            >
              {applyMut.isPending ? t('locations.form.saving') : t('templates.apply')}
            </Button>
          ) : (
            <Button
              type="button"
              disabled={previewMut.isPending || noLocationsPicked}
              onClick={runPreview}
            >
              {previewMut.isPending ? t('locations.form.saving') : t('templates.applyPreviewTitle')}
            </Button>
          )}
        </footer>
      </div>
    </div>
  );
}

// isoMondayString snaps a YYYY-MM-DD (or current Date when called with no
// args) back to its ISO Monday. The apply endpoint only accepts Monday
// week-starts, so this is the snap function for both DatePickerPopovers.
function isoMondayString(input?: string | Date): string {
  const date = input instanceof Date
    ? new Date(input)
    : input
      ? (() => {
          const [y, m, d] = input.split('-').map(Number);
          return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
        })()
      : new Date();
  const jsDay = date.getDay();
  const isoDay = jsDay === 0 ? 7 : jsDay;
  date.setDate(date.getDate() - (isoDay - 1));
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return [y, m, dd].join('-');
}
